import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { Card } from "../molecules/Card"
import { cn } from "../utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> & {
    slick?: boolean
  }
>(({ className, slick = false, asChild, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    className={cn(
      !asChild && "w-fit",
      slick &&
        "underline decoration-dashed decoration-[0.5px] underline-offset-3 hover:cursor-pointer",
      className,
    )}
    asChild={asChild}
    {...props}
  />
))
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const tooltipContentVariants = cva(
  "z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs max-w-md text-center animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin)",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        card: "bg-card text-card-foreground shadow-xl",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const tooltipArrowVariants = cva("", {
  variants: {
    variant: {
      default: "fill-primary",
      card: "fill-card",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

export interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    VariantProps<typeof tooltipContentVariants> {
  children?: React.ReactNode
  className?: string
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, variant, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(tooltipContentVariants({ variant }), className)}
      {...props}
    >
      {props.children}
      <TooltipPrimitive.Arrow className={cn(tooltipArrowVariants({ variant }))} />
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

const TooltipCard = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <TooltipPrimitive.Content asChild>
      <Card
        ref={ref}
        className={cn("z-50 overflow-hidden text-base shadow-lg", className)}
        {...props}
      />
    </TooltipPrimitive.Content>
  ),
)

export { Tooltip, TooltipCard, TooltipContent, TooltipProvider, TooltipTrigger }
